import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

const CNAComponent = () => {
    // JSON data
    const data = {
        cardNumberAlias: "32BC128A51830D26FE4EC363875DBCD44168D1EE577232F5F7A158A30A58A0F1996860"
    };

    // State to handle copy status
    const [copyStatus, setCopyStatus] = useState({ type: '', isCopied: false });

    const handleCopyClick = (type) => {
        const textToCopy = JSON.stringify(data, null, 2);
        navigator.clipboard.writeText(textToCopy)
            .then(() => {
                setCopyStatus({ type, isCopied: true });
                setTimeout(() => {
                    setCopyStatus({ type: '', isCopied: false });
                }, 1000);
            })
            .catch((error) => {
                console.error('Error copying text: ', error);
            });
    };

    return (
        <section className="docs-section" id="CNA">
            <div className="row flexbox">
                <div>
                    <h2 className="section-heading">CNA - Card Number Alias</h2>
                    <h5>Definition:</h5>
                    <p>
                        Card Number Alias (CNA) is the process of converting card data into an alias, which can be used in place of the actual card number.
                    </p>
                    <br />
                    <h5>Purpose:</h5>
                    <p>
                        The primary function of CNA is to streamline the customer experience by linking the alias to the customer's loyalty account. This eliminates the need for customers to swipe a loyalty card during transactions. Instead, the cashier can look up the CNA and credit the associated loyalty account with points from the purchase.
                    </p>
                    <br />
                    <h5>Functionality:</h5>
                    <ul>
                        <li>
                            Cross-Store Integration: CNA is consistent across all stores under the same merchant. When a customer uses their card at any terminal that belongs to the merchant, the CNA generated will be the same.
                        </li>
                        <li>
                            Loyalty Account Linking: The cashier can use the CNA to locate and credit the customer's loyalty account, ensuring points are accurately rewarded for each purchase without the need for a physical loyalty card.
                        </li>
                    </ul>
                    <br />
                    <p>
                        This system simplifies the transaction process for both customers and merchants by leveraging a single, consistent alias across all the merchant's terminals.
                    </p>
                    <br />
                    <h5>Implementation:</h5>
                    <p>
                        The <span style={{ color: "red" }}>"cardNumberAlias" </span> parameter can be found in the <span style={{ color: "red" }}>status </span> endpoint under the <span style={{ color: "red" }}>"cardPaymentData" </span> section, as shown in the example below:
                    </p>
                    <div className="docs-code-block">
                        <pre tabIndex="-1" className="shadow-lg rounded">
                            <code className="language-dockerfile hljs">
                                <div className="row code-header">
                                    <div className="col-md-10 languages">
                                        <span>JSON</span>
                                    </div>
                                    <div className="col-md-2 text-right">
                                        {copyStatus.type !== 'cardNumberAlias' && (
                                            <span className="copy-btn" title="Copy" onClick={() => handleCopyClick("cardNumberAlias")}>
                                                <i className="fa fa-copy"></i>
                                            </span>
                                        )}
                                        {copyStatus.type === 'cardNumberAlias' && copyStatus.isCopied && (
                                            <div className="copy-message">Copied &#x2713;</div>
                                        )}
                                    </div>
                                </div>
                                <div className="code-section">
                                    <SyntaxHighlighter language="json" style={dracula} showLineNumbers>
                                        {JSON.stringify(data, null, 2)}
                                    </SyntaxHighlighter>
                                </div>
                            </code>
                        </pre>
                    </div>
                    <br />
                    <h5>Terminal Configuration:</h5>
                    <p>
                        To enable CNA functionality on payment terminals, customers must request Westpay to activate this feature for all the terminals they wish to use. This is required to ensure the terminals are capable of processing card number aliases.
                    </p>
                </div>
            </div>
        </section>
    )
}

export default CNAComponent;